import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { axiosInstance } from '../helpers'
import { Service } from '../models/Services/types'

// SampleCOI type based on the Django serializer
export interface SampleCOI {
    id: number
    apartment: number
    apartment_name: string
    apartment_id: number
    apartment_ids?: number[]
    apartment_names?: string[]
    file: string
    name: string
    services: Service[]
    // Certificate Holder
    certificate_holder_name: string | null
    certificate_holder_address: string | null
    // Description of Operations
    description_of_operations: string | null
    // General Liability
    gl_each_occurrence: string | null
    gl_damage_to_rented_premises: string | null
    gl_med_exp: string | null
    gl_personal_adv_injury: string | null
    gl_general_aggregate: string | null
    gl_products_comp_op_agg: string | null
    // Auto Liability
    auto_combined_single_limit: string | null
    auto_bodily_injury_per_person: string | null
    auto_bodily_injury_per_accident: string | null
    auto_property_damage: string | null
    // Umbrella
    umbrella_each_occurrence: string | null
    umbrella_aggregate: string | null
    // Workers Comp
    wc_statutory_limits: boolean | null
    wc_el_each_accident: string | null
    wc_el_disease_ea_employee: string | null
    wc_el_disease_policy_limit: string | null
    // Other
    ai_summary: string | null
    uploaded_by: number
    created_date: string
    modified_date: string
}

export interface UpdateSampleCOIRequest {
    name?: string
    service_ids?: number[]
    // Certificate Holder
    certificate_holder_name?: string
    certificate_holder_address?: string
    // Description of Operations
    description_of_operations?: string
    // General Liability
    gl_each_occurrence?: string
    gl_damage_to_rented_premises?: string
    gl_med_exp?: string
    gl_personal_adv_injury?: string
    gl_general_aggregate?: string
    gl_products_comp_op_agg?: string
    // Auto Liability
    auto_combined_single_limit?: string
    auto_bodily_injury_per_person?: string
    auto_bodily_injury_per_accident?: string
    auto_property_damage?: string
    // Umbrella
    umbrella_each_occurrence?: string
    umbrella_aggregate?: string
    // Workers Comp
    wc_statutory_limits?: boolean
    wc_el_each_accident?: string
    wc_el_disease_ea_employee?: string
    wc_el_disease_policy_limit?: string
    apartment_ids?: number[]
}

export interface CreateOrgSampleCOIRequest {
    file: File
    apartment_ids: number[]
    name?: string
}

export interface OrgSampleCOIParams {
    organization?: number | null
}

const QUERY_KEY = ['orgSampleCOIs']

export const useOrgSampleCOIs = (params?: OrgSampleCOIParams) => {
    const queryClient = useQueryClient()
    const [processingIds, setProcessingIds] = useState<Set<number>>(new Set())

    // Build query string for list endpoints (includes all filters)
    const buildQueryString = () => {
        if (!params?.organization) return ''
        const searchParams = new URLSearchParams()
        searchParams.append('organization', params.organization.toString())
        const queryString = searchParams.toString()
        return queryString ? `?${queryString}` : ''
    }

    // Build query string for detail mutations (organization only, no list filters)
    const buildDetailQueryString = () => {
        if (!params?.organization) return ''
        return `?organization=${params.organization}`
    }

    // Get all sample COIs for organization
    const {
        data: sampleCOIs,
        isLoading: isLoadingSampleCOIs,
        error: errorSampleCOIs,
        refetch: refetchSampleCOIs,
    } = useQuery<AxiosResponse<SampleCOI[]>, Error>({
        queryKey: [...QUERY_KEY, params?.organization],
        queryFn: () =>
            axiosInstance.get(`/company/sample_coi/${buildQueryString()}`),
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        enabled: !!params?.organization,
    })

    // Process/analyze a sample COI with AI
    const processSampleCOI = useMutation({
        mutationFn: async (sampleCOIId: number) => {
            setProcessingIds((prev) => new Set(prev).add(sampleCOIId))
            return axiosInstance.post(
                `/company/sample_coi/${sampleCOIId}/process_coi/${buildDetailQueryString()}`,
            )
        },
        onSuccess: (_, sampleCOIId) => {
            setProcessingIds((prev) => {
                const next = new Set(prev)
                next.delete(sampleCOIId)
                return next
            })
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            toast.success('Sample COI analyzed successfully')
        },
        onError: (error: any, sampleCOIId) => {
            setProcessingIds((prev) => {
                const next = new Set(prev)
                next.delete(sampleCOIId)
                return next
            })
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to analyze sample COI'
            toast.error(errorMessage)
        },
    })

    const createSampleCOI = useMutation({
        mutationFn: async (request: CreateOrgSampleCOIRequest) => {
            const formData = new FormData()
            formData.append('file', request.file)
            if (request.name) {
                formData.append('name', request.name)
            }
            formData.append(
                'apartment_ids',
                JSON.stringify(request.apartment_ids),
            )
            return axiosInstance.post<SampleCOI>(
                `/company/sample_coi/${buildQueryString()}`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } },
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            queryClient.invalidateQueries({ queryKey: ['sampleCOIs'] })
            toast.success('Sample COI uploaded successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to upload sample COI'
            toast.error(errorMessage)
        },
    })

    const uploadSampleCOIs = async (files: File[], apartmentIds: number[]) => {
        const results = await Promise.allSettled(
            files.map((file) =>
                createSampleCOI.mutateAsync({
                    file,
                    apartment_ids: apartmentIds,
                }),
            ),
        )
        return results
    }

    // Update a sample COI
    const updateSampleCOI = useMutation({
        mutationFn: async ({
            id,
            data,
        }: {
            id: number
            data: UpdateSampleCOIRequest
        }) => {
            return axiosInstance.patch<SampleCOI>(
                `/company/sample_coi/${id}/${buildDetailQueryString()}`,
                data,
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            queryClient.invalidateQueries({ queryKey: ['sampleCOIs'] })
            toast.success('Sample COI updated successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to update sample COI'
            toast.error(errorMessage)
        },
    })

    // Delete a sample COI
    const deleteSampleCOI = useMutation({
        mutationFn: async (id: number) => {
            return axiosInstance.delete(
                `/company/sample_coi/${id}/${buildDetailQueryString()}`,
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            toast.success('Sample COI deleted successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to delete sample COI'
            toast.error(errorMessage)
        },
    })

    const sendToVendors = useMutation({
        mutationFn: async ({
            id,
            vendorIds,
        }: {
            id: number
            vendorIds: number[]
        }) => {
            return axiosInstance.post(
                `/company/sample_coi/${id}/send_to_vendors/${buildDetailQueryString()}`,
                { vendor_ids: vendorIds },
            )
        },
        onSuccess: (response) => {
            const data = response.data || {}
            const sentCount = data.sent?.length || 0
            const skippedCount = data.skipped?.length || 0
            if (sentCount > 0) {
                toast.success(
                    `Sample COI sent to ${sentCount} vendor(s)${
                        skippedCount > 0
                            ? ` (${skippedCount} skipped — no email)`
                            : ''
                    }`,
                )
            } else {
                toast.warning(
                    'No vendors were emailed. Add an email address and try again.',
                )
            }
        },
        onError: (error: any) => {
            toast.error(
                error?.response?.data?.error ||
                    error?.response?.data?.message ||
                    'Failed to send sample COI to vendors',
            )
        },
    })

    return {
        sampleCOIs: sampleCOIs?.data || [],
        isLoadingSampleCOIs,
        errorSampleCOIs,
        refetchSampleCOIs,
        processSampleCOI,
        createSampleCOI,
        uploadSampleCOIs,
        updateSampleCOI,
        deleteSampleCOI,
        sendToVendors,
        isProcessing: processSampleCOI.isLoading,
        isUploading: createSampleCOI.isLoading,
        isUpdating: updateSampleCOI.isLoading,
        isDeleting: deleteSampleCOI.isLoading,
        isSending: sendToVendors.isLoading,
        processingIds,
    }
}
